import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Scale } from 'lucide-react';
import { WarehouseInventory } from '../../types/entities';

interface InventoryTableProps {
  inventory: WarehouseInventory[];
  loading: boolean;
  onViewHistory: (itemId: number, itemName: string) => void;
  onReconcile?: (itemId: number, itemName: string, systemStock: number, unit: string) => void;
}

export const InventoryTable: React.FC<InventoryTableProps> = ({
  inventory,
  loading,
  onViewHistory,
  onReconcile,
}) => {
  const { t } = useTranslation();

  const formatUnit = (unit: string) => {
    return t(`items.units.${unit}`, { defaultValue: unit });
  };

  if (loading) {
    return <div className="text-center py-8">{t('common.loading')}</div>;
  }

  if (inventory.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('warehouses.noInventory')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b">
          <tr className="text-left">
            <th className="pb-3 table-header">{t('items.name')}</th>
            <th className="pb-3 table-header text-right">{t('warehouses.quantityIn')}</th>
            <th className="pb-3 table-header text-right">{t('warehouses.quantityOut')}</th>
            <th className="pb-3 table-header text-right">{t('warehouses.currentStock')}</th>
            <th className="pb-3 table-header">{t('common.unit')}</th>
            <th className="pb-3 table-header">{t('warehouses.lastUpdated')}</th>
            <th className="pb-3 table-header text-center">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {inventory.map((item) => (
            <tr
              key={item.id}
              className={`border-b last:border-0 ${
                item.currentStock < 0
                  ? 'bg-red-50'
                  : item.currentStock < 10 && item.currentStock > 0
                  ? 'bg-yellow-50'
                  : ''
              }`}
            >
              <td className="py-3 font-medium">{item.item?.name || `Item #${item.itemId}`}</td>
              <td className="py-3 text-right text-green-600">+{item.quantityIn}</td>
              <td className="py-3 text-right text-red-600">-{item.quantityOut}</td>
              <td className="py-3 text-right font-bold">
                <span
                  className={
                    item.currentStock < 0
                      ? 'text-red-600'
                      : item.currentStock < 10
                      ? 'text-yellow-600'
                      : ''
                  }
                >
                  {item.currentStock}
                </span>
              </td>
              <td className="py-3">{formatUnit(item.unit)}</td>
              <td className="py-3 text-gray-500 text-sm">
                {new Date(item.lastUpdated).toLocaleDateString('vi-VN')}
              </td>
              <td className="py-3 text-center">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => onViewHistory(item.itemId, item.item?.name || `Item #${item.itemId}`)}
                    className="p-1 hover:bg-gray-200 rounded"
                    title={t('warehouse.inventory.viewHistory')}
                  >
                    <Eye className="w-5 h-5 text-blue-600" />
                  </button>
                  {onReconcile && (
                    <button
                      onClick={() =>
                        onReconcile(
                          item.itemId,
                          item.item?.name || `Item #${item.itemId}`,
                          Number(item.currentStock),
                          item.unit
                        )
                      }
                      className="p-1 hover:bg-gray-200 rounded"
                      title={t('warehouse.inventory.reconcile')}
                    >
                      <Scale className="w-5 h-5 text-amber-600" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
